import { Model } from '@vuex-orm/core'
import { Lesson, Course, Bookable, Grade } from 'src/store/models/timetable/index.js'
import { UserParent, UserCourse } from 'src/store/models/pivot/index.js'
import { School } from 'src/store/models/index.js'
import tracking from 'src/config/tracking/index.js'
import Users from 'src/api/users.js'
import GlobalPrototypes from '@/config/services/globalPrototypes.ts'

export class User extends Model {
  static entity = 'users'
  static fetchAllUrl = '/users'

  static fields () {
    return {
      id: this.attr(null),
      uuid: this.attr(null),
      firstname: this.attr(''),
      lastname: this.attr(''),
      email: this.attr(''),
      phone: this.attr(''),
      password: this.attr(''),
      displayname: this.attr(''),
      subtitle: this.string(''),
      salutation: this.attr(''),
      sex: this.attr(''),
      title: this.attr(''),
      username: this.attr(null),
      locale: this.attr(''),
      code: this.attr(''),
      password_changed_at: this.attr(''),
      loggedin_at: this.attr(''),
      is_trackable: this.boolean(false),
      tfa_mode: this.attr('email'),
      deletion_email: this.attr(null),
      roles: this.attr([]),
      permissions: this.attr([]),
      type: this.attr(''),
      primary_role: this.attr(null),
      access_token: this.attr(''),
      refresh_token: this.attr(''),
      child_pivot: this.hasMany(UserParent, 'parent_id'),
      childs: this.belongsToMany(User, UserParent, 'parent_id', 'user_id'),
      parent_pivot: this.hasMany(UserParent, 'user_id'),
      parents: this.belongsToMany(User, UserParent, 'user_id', 'parent_id'),
      grade_id: this.attr(null),
      grade: this.belongsTo(Grade, 'grade_id'),
      shortcut_id: this.attr(null),
      shortcut: this.attr(null),
      school_id: this.attr(null),
      school: this.belongsTo(School, 'school_id'),
      bookable: this.belongsTo(Bookable, 'bookable_id'),
      courses: this.belongsToMany(Course, UserCourse, 'user_id', 'course_id'),
      courses_pivot: this.hasManyBy(UserCourse, 'course_id'),
      lessons: this.hasManyThrough(Lesson, Course, 'user_id', 'course_id'),
      is_locked: this.boolean(false),
      is_admin: this.boolean(false),
      can: this.attr({}),
      meta: this.attr({}),
      properties: this.attr({}),
      state: this.attr(''),
      method: this.attr(''),
      registered_at: this.attr(null),

      // if user has sdui-manage permission
      created_at: this.attr(null),
      code_chunk: this.attr(null),
      last_seen_at: this.attr(null),
      expire_at: this.attr(null)
    }
  }

  static mutators () {
    return {
      properties: props => {
        if (!props) return
        return Object.keys(props).reduce((acc, key) => {
          const getNewValue = value => {
            if (value === '0') return false
            if (value === '1') return true
            return value
          }
          return {
            ...acc,
            [key]: getNewValue(props[key])
          }
        }, {})
      }
    }
  }

  static async fetchUsers (data) {
    return Users.getAllUsers({ params: { page: data.page, search: data.search } })
  }

  static linkToTimetable (user) {
    return {
      name: 'timetable',
      query: {
        entity: 'users',
        id: user.id
      }
    }
  }

  static async changePassword (currentPassword, newPassword, userId) {
    const res = await Users.putUpdatePassword(userId, {
      password_current: currentPassword,
      password: newPassword
    })

    User.insertOrUpdate({ data: res.data })
    return res.data
  }

  static async changeName ({ firstname, lastname, title }, userId) {
    const res = await Users.putUpdateName(userId, { firstname, lastname, title })
    User.insertOrUpdate({ data: res.data })
    return res.data
  }

  static async changeEmail ({ email, password_current }, userId) {
    const res = await Users.putUpdateEmail(userId, { email, password_current })
    User.insertOrUpdate({ data: res.data })
    return res.data
  }

  static async changePhone (phone, id) {
    const res = await Users.putSingleUser(id, { phone: phone })
    User.insertOrUpdate({ data: res.data })
    return res.data
  }

  static async changeUsername ({ username, password_current }, userId) {
    const res = await Users.putUpdateUsername(userId, { username, password_current })
    User.insertOrUpdate({ data: res.data })
    return res.data
  }

  static async changeGrade ({ grade }, userId) {
    const res = await Users.putTtGrade(userId, { grade })
    User.insertOrUpdate({ data: res.data })
    return res.data
  }

  static async getUserProfile (userId) {
    if (userId === 0) return
    const res = await Users.getSingleUser(userId)
    User.insertOrUpdate({ data: res.data })
    return res.data
  }

  static async userUserPropsById (userId, props) {
    if (!userId || !props) return
    const res = await Users.putSettings(userId, props, { showMessages: false })
    User.update({
      where: userId,
      data: {
        properties: res.data
      }
    })
  }

  static async updateProps (props, showMessages = false) {
    const userId = this.store().state.users.me_id
    const res = await Users.putSettings(userId, props, { showMessages: showMessages })
    User.update({
      where: this.store().state.users.me_id,
      data: {
        properties: res.data
      }
    })
  }

  static async resetTimetable (userId) {
    return Users.postTtCoursesReset(userId)
  }

  static async connectChild (parentId, childId, relationship) {
    const { data } = await Users.postAssignChild(parentId, childId)
    await Users.putAssignChild(parentId, { ...data, family_relation: { value: relationship } })
    User.insertOrUpdate({ data })
  }

  static async addChild (child) {
    const id = this.store().state.users.me_id
    const res = await Users.postAssignChildAsObject(id, child)
    User.insertOrUpdate({ data: res.data })
    return res.data
  }

  static async editChild (child) {
    const id = this.store().state.users.me_id
    const res = await Users.putAssignChild(id, child)
    User.insertOrUpdate({ data: res.data })
  }

  static async checkChildCode (code) {
    return Users.postCheckCodeChild(this.store().state.users.me_id, { code })
  }

  is (user) {
    return this.id === user.id
  }

  isnot (user) {
    return !this.is(user)
  }

  static async isTrackable (isTrackable) {
    const id = this.store().getters['users/me'].id
    const res = await Users.putChangeStatistic(id, { is_trackable: isTrackable }, { showMessages: false })
    User.insertOrUpdate({ data: res.data })
    res.data.is_trackable ? tracking.startTracking(res.data) : tracking.stopTracking()
    return res
  }

  static async setCountlyHashStatus (countlyUsesHash) {
    const id = this.store().getters['users/me'].id
    const res = await Users.putSingleUser(id, { countly_uses_hash: countlyUsesHash }, { showMessages: false })
    User.insertOrUpdate({ data: res.data })
    return res
  }

  static async undoExpire (userId) {
    const res = await Users.postUndoExpire(userId)
    User.insertOrUpdate({ data: res.data })
    return res
  }

  static async deleteAccount ({ email, password }, userId = null) {
    const id = userId || this.store().state.users.me_id
    const res = await Users.postUserDeletionRequest(id, { deletion_email: email, password })
    User.insertOrUpdate({ data: res.data })
    return res.data
  }

  static async cancelAccountDeletion (userId = null) {
    const id = userId || this.store().state.users.me_id
    const res = await Users.putRejectUserDeletionRequest(id)
    const userClone = {
      id: res.data.id,
      meta: {
        deletion_request_submitted: res.data.deletion_request_submitted,
        deletion_request_approved: res.data.deletion_request_approved
      }
    }

    User.insertOrUpdate({ data: userClone })
    this.store().dispatch('users/getSelf')
    return res.data
  }

  static async deleteParent (parentId, userId) {
    const res = await Users.postRemoveParent(userId, parentId)
    await User.insertOrUpdate({ data: res.data })
    await this.store().dispatch('users/getSelf')
    return res.data
  }

  static async deleteChild (childId, userId) {
    const res = await Users.postRemoveChild(userId, childId)
    User.insertOrUpdate({ data: res.data })
    return res.data
  }

  static async setShortCut (userId, shortCut) {
    const res = await Users.putShortcut(userId, { shortcut: shortCut })
    await User.insertOrUpdate({ data: res.data })
    return res.data
  }

  static async setLanguage (userId, language) {
    const res = await Users.putUpdateLocale(userId, { locale: language })
    await User.insertOrUpdate({ data: res.data })
    /* localize functions */
    await GlobalPrototypes.switchLocale(language)
    this.store().dispatch('users/getSelf', { useCache: false })

    return res.data
  }

  static async getAvailableShortcuts (userId) {
    const res = await Users.getAvailableShortcut(userId)
    return res.data
  }

  static async getAvailableGrades (userId) {
    const res = await Users.getTtAvailableGrades(userId)
    return res.data
  }

  static async generateParentCode (userId) {
    const res = await Users.postRequestCodeChild(userId)
    await User.insertOrUpdate({ data: res.data })
    return res.data
  }

  static async waitForUserFetch () {
    // used let because it didnt worked with map
    const sleep = (ms) => {
      return new Promise(resolve => setTimeout(resolve, ms))
    }

    for (let i = 0; i < 30; i++) {
      const user = this.store().getters['users/me']
      if (user) return user

      await sleep(100)
    }

    return null
  }
}

export default User
